/**
 * YAML conditions node -> builder model.
 *
 * `yamlNodeToBuilderModel` takes a value already parsed from YAML (string,
 * array, or {and|or: ...} object) and returns the structured BuilderNode tree,
 * or `null` when the input cannot be represented losslessly in the builder (in
 * which case the caller should fall back to the raw-YAML "advanced" editor).
 */

import {
	BuilderNode,
	ConditionOperator,
	ConditionRule,
	ConditionValue,
	TimeUnit,
	emptyGroup,
	newConditionId,
} from "./types";

/* ---- string-rule tokenizer -------------------------------------------- */

const INFIX_OPERATORS: ConditionOperator[] = [
	"=",
	"!=",
	">",
	"<",
	">=",
	"<=",
	"contains",
	"not_contains",
];

interface TokenizedRule {
	fieldName: string;
	operator: ConditionOperator;
	rawValue: string;
}

/**
 * Split a rule string into { field, operator, value }. The DSL is whitespace
 * delimited: the field is a single token, the operator the next token, and the
 * value is everything after it (values may contain spaces, e.g. "grand ronde").
 * Returns null when the string isn't a well-formed rule.
 */
export const tokenizeRule = (input: string): TokenizedRule | null => {
	const trimmed = input.trim();
	if (!trimmed) return null;

	const parts = trimmed.split(/\s+/);
	if (parts.length < 2) return null;

	const fieldName = parts[0];
	const opToken = parts[1];

	// Suffix existence operators take no value: "<field> ?" / "<field> !?".
	if (opToken === "?" || opToken === "!?") {
		if (parts.length !== 2) return null;
		return { fieldName, operator: opToken, rawValue: "" };
	}

	if (!INFIX_OPERATORS.includes(opToken as ConditionOperator)) return null;
	const rawValue = parts.slice(2).join(" ").trim();
	if (!rawValue) return null;

	return { fieldName, operator: opToken as ConditionOperator, rawValue };
};

/* ---- value parsing ---------------------------------------------------- */

const UNIT_ALIASES: Record<string, TimeUnit> = {
	second: "second",
	seconds: "second",
	minute: "minute",
	minutes: "minute",
	hour: "hour",
	hours: "hour",
	day: "day",
	days: "day",
	week: "week",
	weeks: "week",
	month: "month",
	months: "month",
	year: "year",
	years: "year",
};

const VARIABLE_RE = /^\{\{\s*([^{}]+?)\s*\}\}$/;
const NOW_RE = /^NOW\(\)\s*(?:([+-])\s*(\d+)\s*([a-zA-Z]+))?$/i;
const VAR_OFFSET_RE = /^([A-Za-z_][\w.]*)\s*([+-])\s*(\d+)\s*([a-zA-Z]+)$/;
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?$/;

/** Parse the right-hand side of a rule into a structured ConditionValue. */
export const parseRuleValue = (raw: string): ConditionValue => {
	const value = raw.trim();

	const variableMatch = value.match(VARIABLE_RE);
	if (variableMatch) return { kind: "variable", name: variableMatch[1].trim() };

	const nowMatch = value.match(NOW_RE);
	if (nowMatch) {
		if (!nowMatch[1]) return { kind: "nowRelative", offset: 0, unit: "day" };
		const unit = UNIT_ALIASES[nowMatch[3].toLowerCase()];
		if (unit) {
			const offset = (nowMatch[1] === "-" ? -1 : 1) * parseInt(nowMatch[2], 10);
			return { kind: "nowRelative", offset, unit };
		}
	}

	const varOffsetMatch = value.match(VAR_OFFSET_RE);
	if (varOffsetMatch) {
		const unit = UNIT_ALIASES[varOffsetMatch[4].toLowerCase()];
		if (unit) {
			const offset = (varOffsetMatch[2] === "-" ? -1 : 1) * parseInt(varOffsetMatch[3], 10);
			return { kind: "varRelative", varName: varOffsetMatch[1], offset, unit };
		}
	}

	if (ISO_DATETIME_RE.test(value)) return { kind: "datetime", iso: value };

	// Plain literal (numbers and strings both round-trip as text).
	return { kind: "literal", value };
};

/* ---- node mapping ----------------------------------------------------- */

const ruleFromString = (input: string): ConditionRule | null => {
	const tokens = tokenizeRule(input);
	if (!tokens) return null;
	const rule: ConditionRule = {
		id: newConditionId(),
		fieldName: tokens.fieldName,
		operator: tokens.operator,
	};
	if (tokens.operator !== "?" && tokens.operator !== "!?") {
		rule.value = parseRuleValue(tokens.rawValue);
	}
	return rule;
};

/**
 * Convert a YAML-parsed conditions node into the builder tree.
 * Returns null if any part can't be represented (caller keeps raw YAML).
 */
export const yamlNodeToBuilderModel = (node: unknown): BuilderNode | null => {
	if (node === null || node === undefined) return null;

	if (typeof node === "string") {
		return ruleFromString(node);
	}

	if (Array.isArray(node)) {
		const group = emptyGroup("and");
		for (const item of node) {
			const child = yamlNodeToBuilderModel(item);
			if (!child) return null; // un-representable child -> bail to YAML
			group.children.push(child);
		}
		if (group.children.length === 0) return null;
		if (group.children.length === 1) return group.children[0];
		return group;
	}

	if (typeof node === "object") {
		const keys = Object.keys(node as Record<string, unknown>);
		if (keys.length !== 1) return null;
		const key = keys[0];
		if (key !== "and" && key !== "or") return null;

		const rawChildren = (node as Record<string, unknown>)[key];
		const childArray = Array.isArray(rawChildren) ? rawChildren : [rawChildren];

		const group = emptyGroup(key);
		for (const item of childArray) {
			const child = yamlNodeToBuilderModel(item);
			if (!child) return null;
			group.children.push(child);
		}
		if (group.children.length === 0) return null;
		// A one-child group is semantically equal to that child; normalise so the
		// model round-trips idempotently with serialize's single-child unwrap.
		if (group.children.length === 1) return group.children[0];
		return group;
	}

	return null;
};
